import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { RichEditor } from "@/components/RichEditor";
import { toast } from "sonner";
import { EnvelopeSimpleIcon as EnvelopeSimple, FloppyDiskIcon as FloppyDisk } from "@phosphor-icons/react";

const VARS = ["numero", "data", "cliente", "modalita_addebito", "luogo", "tecnico"];

export function ReportSendSection() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/interventi-config/report-send").then(({ data }) => setCfg({ ...data, cc: (data.cc || []).join(", "), bcc: (data.bcc || []).join(", ") })).catch(() => {});
  useEffect(() => { load(); }, []);

  if (!cfg) return null;
  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/interventi-config/report-send", {
        mittente: cfg.mittente, cc: cfg.cc, bcc: cfg.bcc,
        oggetto_template: cfg.oggetto_template,
        corpo_html: cfg.corpo_html || "", firma_html: cfg.firma_html || "",
        allega_pdf: cfg.allega_pdf,
      });
      setCfg({ ...data, cc: (data.cc || []).join(", "), bcc: (data.bcc || []).join(", ") });
      toast.success("Configurazione invio rapportino salvata");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Card className="rounded-sm border-border shadow-none p-6" data-testid="report-send-section">
      <div className="flex items-start gap-3 mb-5">
        <div className="h-10 w-10 rounded-sm bg-secondary flex items-center justify-center"><EnvelopeSimple size={20} weight="bold" className="text-primary" /></div>
        <div>
          <h3 className="font-heading font-semibold">Invio rapportino</h3>
          <p className="text-xs text-muted-foreground">Valori predefiniti per l'email di invio del rapportino al cliente (modificabili prima di ogni invio).</p>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-3 mb-4">
        <div>
          <Label className="text-[10px] uppercase tracking-wider">Mittente (opz.)</Label>
          <Input value={cfg.mittente || ""} onChange={(e) => set("mittente", e.target.value)} placeholder="Casella M365 predefinita" className="mt-1 rounded-sm h-9" data-testid="rs-mittente" />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider">CC fissi</Label>
          <Input value={cfg.cc || ""} onChange={(e) => set("cc", e.target.value)} placeholder="email separate da , o ;" className="mt-1 rounded-sm h-9" data-testid="rs-cc" />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider">CCN fissi</Label>
          <Input value={cfg.bcc || ""} onChange={(e) => set("bcc", e.target.value)} placeholder="email separate da , o ;" className="mt-1 rounded-sm h-9" data-testid="rs-bcc" />
        </div>
      </div>

      <div className="mb-4">
        <Label className="text-[10px] uppercase tracking-wider">Oggetto predefinito</Label>
        <Input value={cfg.oggetto_template || ""} onChange={(e) => set("oggetto_template", e.target.value)} className="mt-1 rounded-sm h-9" data-testid="rs-oggetto" />
      </div>

      <div className="mb-4">
        <Label className="text-[10px] uppercase tracking-wider">Testo email predefinito</Label>
        <div className="mt-1"><RichEditor value={cfg.corpo_html || ""} onChange={(v) => set("corpo_html", v)} testid="rs-corpo" placeholder="Testo email (supporta grassetto, corsivo, liste, link…)" /></div>
      </div>

      <div className="mb-4">
        <Label className="text-[10px] uppercase tracking-wider">Firma predefinita</Label>
        <div className="mt-1"><RichEditor value={cfg.firma_html || ""} onChange={(v) => set("firma_html", v)} testid="rs-firma" placeholder="Firma (HTML)" /></div>
      </div>

      <p className="text-xs text-muted-foreground mb-4">
        Variabili disponibili: {VARS.map((v) => <code key={v} className="mx-1 px-1 py-0.5 rounded-sm bg-secondary text-[11px]">{`{{${v}}}`}</code>)}
      </p>

      <div className="flex items-center justify-between border border-border rounded-sm px-3 py-2 mb-6">
        <span className="text-sm">Allega sempre il PDF del rapportino</span>
        <Switch checked={!!cfg.allega_pdf} onCheckedChange={(v) => set("allega_pdf", v)} data-testid="rs-allega-pdf" />
      </div>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} className="rounded-sm gap-2" data-testid="rs-save-btn"><FloppyDisk size={16} weight="bold" /> {saving ? "Salvataggio…" : "Salva configurazione"}</Button>
      </div>
    </Card>
  );
}
